import { useCallback } from 'react';
import { useSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import {
  getJobInviteNotificationMessage,
  getConnectionNotificationMessage,
  getJobLinkRequestNotificationMessage,
} from 'src/utils/notifications.utils';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { ConnectionActionOption } from 'src/services/connections/connections.types';
import { showConnectionActionModal } from 'src/store/slices/connections/connectionsSlice';
import { useMarkAllAsReadMutation } from 'src/services/notifications/notifications.service';
import { useActionConnectionRequestMutation } from 'src/services/connections/connections.service';
import {
  Notification,
  NotificationType,
  NotificationCategory,
} from 'src/services/notifications/notifications.types';
import {
  showJobLinkActionModal,
  showJobVerificationModal,
  showCandidateJobRequestsModal,
} from 'src/store/slices/jobs/jobsSlice';

// ----------------------------------------------------------------------

type NotificationItemProps = {
  notification: Notification;
  closeDrawer: VoidFunction;
};

// const getNotificationTitle = (notification: Notification) => {
//   if (notification.category === NotificationCategory.JOB) {
//     return i18n.t()
//   }
// }

const getNotificationSubtitle = (notification: Notification) => {
  console.log(notification);
  if (notification.category === NotificationCategory.CONNECTION) {
    return getConnectionNotificationMessage(notification);
  }

  if (notification.category === NotificationCategory.JOB) {
    return getJobLinkRequestNotificationMessage(notification);
  }

  if (notification.category === NotificationCategory.JOB_INVITE) {
    return getJobInviteNotificationMessage(notification);
  }

  return notification.category;
};

export default function NotificationItem({ notification, closeDrawer }: NotificationItemProps) {
  const { t } = useTranslate();

  const dispatch = useAppDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const router = useRouter();

  const [actionConnectionRequest, { isLoading: isRejectingConnection }] =
    useActionConnectionRequestMutation();

  const [markAsRead] = useMarkAllAsReadMutation();

  const onAcceptConnectionRequest = useCallback(
    (connectionId: string) => {
      dispatch(
        showConnectionActionModal({ requestId: connectionId, notificationId: notification.id })
      );

      closeDrawer();
    },
    [closeDrawer, dispatch, notification.id]
  );

  const onRejectConnectionRequest = useCallback(
    async (connectionId: string) => {
      try {
        await actionConnectionRequest({
          connectionId,
          action: {
            action: ConnectionActionOption.Reject,
          },
        }).unwrap();

        closeDrawer();
        markAsRead({ notifications: [notification.id] });
        enqueueSnackbar(t(`connections.api.action.success-${ConnectionActionOption.Reject}`));
      } catch (e) {
        console.error(e);

        enqueueSnackbar(t('connections.api.action.default_error'), { variant: 'error' });
      }
    },
    [actionConnectionRequest, closeDrawer, enqueueSnackbar, markAsRead, notification.id, t]
  );

  const renderAvatar = (
    <ListItemAvatar>
      {/* {notification.avatarUrl ? (
        <Avatar src={notification.avatarUrl} sx={{ bgcolor: 'background.neutral' }} />
      ) : ( */}
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'background.neutral',
        }}
      >
        <Box
          component="img"
          src="/assets/icons/notification/ic_mail.svg"
          sx={{ width: 24, height: 24 }}
        />
      </Stack>
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={reader(notification.metadata.title ?? 'New Notification')}
      secondary={
        <Stack
          direction="row"
          alignItems="center"
          sx={{ typography: 'caption', color: 'text.disabled' }}
          divider={
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: 'currentColor',
                mx: 0.5,
                borderRadius: '50%',
              }}
            />
          }
        >
          {/* {fToNow(notification.metadata.)} */}
          {getNotificationSubtitle(notification)}
        </Stack>
      }
    />
  );

  <Box
    sx={{
      top: 26,
      width: 8,
      height: 8,
      right: 20,
      borderRadius: '50%',
      bgcolor: 'info.main',
      position: 'absolute',
    }}
  />;

  const connectionAction = (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() =>
          onAcceptConnectionRequest(notification.metadata?.metadata?.request_id as string)
        }
      >
        {t('common.accept')}
      </Button>
      <LoadingButton
        size="small"
        variant="outlined"
        onClick={() =>
          onRejectConnectionRequest(notification.metadata?.metadata?.request_id as string)
        }
        loading={isRejectingConnection}
      >
        {t('common.decline')}
      </LoadingButton>
    </Stack>
  );

  const jobLinkAction = (notificationObj: Notification) => {
    if (notification.type === NotificationType.JOB_CLIENT_VERIFIED) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              router.push(
                `${paths.dashboard.jobs.root}/${
                  notificationObj.metadata?.metadata?.job_id as string
                }`
              );
              closeDrawer();
              markAsRead({ notifications: [notification.id] });
            }}
          >
            {t('common.view')}
          </Button>
        </Stack>
      );
    }

    if (notification.type === NotificationType.JOB_LINK_REQUEST_INITIATED) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              dispatch(
                showJobLinkActionModal({
                  requestId: notificationObj.metadata?.metadata?.link_request_id as string,
                  notificationId: notification.id,
                })
              );

              closeDrawer();
            }}
          >
            {`${t('common.manage')}`}
          </Button>
        </Stack>
      );
    }

    if (notification.type === NotificationType.JOB_REQUIRES_APPROVAL) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              dispatch(showJobVerificationModal());
              closeDrawer();
              markAsRead({ notifications: [notification.id] });
            }}
          >
            {`${t('common.manage')}`}
          </Button>
        </Stack>
      );
    }

    return null;
  };

  const jobInviteAction = (notificationObj: Notification) => {
    if (notification.type === NotificationType.JOB_INVITE_INITIATED) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              dispatch(showCandidateJobRequestsModal());
              closeDrawer();
              markAsRead({ notifications: [notificationObj.id] });
            }}
          >
            {`${t('common.view')}`}
          </Button>
        </Stack>
      );
    }

    return null;
  };

  return (
    <ListItemButton
      disableRipple
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
      }}
    >
      {/* {renderUnReadBadge} */}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }}>
        {renderText}

        {notification.type === NotificationType.INITIATE_CONNECTION_REQUEST && connectionAction}

        {notification.category === NotificationCategory.JOB && jobLinkAction(notification)}

        {notification.category === NotificationCategory.JOB_INVITE && jobInviteAction(notification)}
      </Stack>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function reader(data: string) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        '& p': { typography: 'body2', m: 0 },
        '& a': { color: 'inherit', textDecoration: 'none' },
        '& strong': { typography: 'subtitle2' },
      }}
    />
  );
}
